import { Component, Vue, Prop } from "vue-property-decorator";
import { endorsementFormApi } from "@/providers/apis/endorsement-form";
import { utils } from "@/utils/utils";
import { appConfigService } from "@/providers/services/app/app-config";
import {
  User,
  dateRange,
  STATUS,
} from "@/models/common-interface/common.interface";
import AppLoaderComponent from "../app-loader/app-loader.component.vue";
import ErrorPopupComponent from "../error-popup/error-popup.component.vue";
import DateRangePicker from "@/components/date-range-picker-local/components/DateRangePicker.vue";
import FileUploadComponent from "../file-upload/file-upload.component.vue";
import moment from "moment";

@Component({
  name: "reviewer-endorsement-preview-form",
  // Host a vee-validate validator so the FileUploadComponent (which injects
  // "$validator") has one to use — this component has no v-validate fields of
  // its own to create one automatically.
  $_veeValidate: { validator: "new" },
  components: {
    AppLoaderComponent,
    ErrorPopupComponent,
    DateRangePicker,
    FileUploadComponent,
  },
})
export default class ReviewerEndorsementPreviewForm extends Vue {
  // When rendered inside the applicant "My Applications" page, the row data is
  // passed in and reviewer controls are hidden (read-only view).
  @Prop({ default: null }) applicationPreviewForm!: any;
  @Prop({ default: false }) isUserApplication!: boolean;
  // When rendered inside the Peers profile page, it loads by route id but still
  // shows the reviewer's Admin Review panel (like the other forms' peer preview).
  @Prop({ default: false }) isPeerPreview!: boolean;

  // Only the applicant's own "My Applications" view is read-only; the reviewer's
  // peer preview keeps the Admin Review panel and controls.
  get isReadOnly(): boolean {
    return this.isUserApplication;
  }

  public activeFormTab = "application";

  // Reviewers manage the certificate in the Admin Review panel, which sits
  // alongside the form, so the tab is only for read-only views — and only once
  // a certificate actually exists.
  get showCertificateTab(): boolean {
    return this.isReadOnly && !!this.trainingCertificate;
  }

  // Opened from a peer profile, leaving should land back on that profile rather
  // than the CPRS-J list. The peers page forwards the peer id and any report
  // filters into this route's query, so handing the query back restores it.
  /*
   * Deleting is gated by the same runtime flag as every other reviewer
   * preview, so the control appears in the environments that allow it and
   * nowhere else.
   */
  public showDelete = appConfigService.getShowDelete();
  public deleteSubmittedAt = "";
  public deleteSuccess = false;

  public confirmDelete() {
    this.deleteSubmittedAt = this.applicationData.submittedAt;
    this.deleteSuccess = false;
    this.$bvModal.show("delete-application-modal-endorsement-preview");
  }

  public cancelDelete() {
    this.deleteSuccess = false;
    this.$bvModal.hide("delete-application-modal-endorsement-preview");
  }

  public submitDelete() {
    this.isLoading = true;
    // Same id the edit path uses. In the peers context the route's :peerId
    // segment carries the application id (the peer's own id travels in
    // query.id), so applicantId is the right fallback in both places.
    const id = this.applicationData?.id || this.applicantId;
    endorsementFormApi
      .deleteEndorsementApplication(id)
      .then(() => {
        this.isLoading = false;
        this.deleteSuccess = true;
        setTimeout(() => {
          this.$bvModal.hide("delete-application-modal-endorsement-preview");
          this.deleteSuccess = false;
          // Back to wherever this form was opened from - the peer profile when
          // it came from Peers, the endorsement list otherwise.
          this.$router.push(this.returnRoute);
        }, 2000);
      })
      .catch((error) => {
        this.errorMessage = utils.errorMessage(error);
        this.isLoading = false;
      });
  }

  get returnRoute(): any {
    return this.isPeerPreview
      ? { path: "/reviewer-dashboard/peers-preview", query: this.$route.query }
      : { path: "/reviewer-dashboard/cprs-j" };
  }

  public applicantId = "";
  public applicationData: any = null;
  public formData: any = null;
  public isLoading = true;
  public errorMessage = "";
  public userDetails!: User;

  public approveStatus = "";
  public approveComment = "";
  // Read-only display of the saved comment + reviewer status history.
  public historys: any[] = [];
  public trainingStatus = "";
  public trainingDates = "";
  public trainingLocation = "";
  public trainingCertificate = "";

  /*
   * The certificate the endorsement ISSUES. Kept separate from
   * trainingCertificate above, which is the evidence the applicant submits and
   * lives in the rubric. This one lives in the endorsement's own columns.
   */
  public issuedCertificateFiles: any[] = [];
  public issuedCertificateAttachment: any = {
    payloadName: "endorsementCertificate",
    files: [],
  };
  public issuedCertificateFile: any = null;
  // Raw file(s) chosen in the uploader; uploaded on Submit (deferred upload).

  // File-upload model for the Training Certificate (PDF).
  public trainingCertificateAttachment: any = {
    payloadName: "trainingCertificate",
    files: [],
  };

  // Date-range picker model for Training Dates.
  public trainingDateRange: dateRange = {} as dateRange;
  public localeDate = {
    format: "mm/dd/yyyy",
    applyLabel: "Apply",
    firstDay: 0,
  };
  public isSubmitting = false;
  public submitError = "";

  // Inline edit of the applicant's basic details (reviewer only).
  public isEditing = false;
  public savingEdit = false;
  public editBasicDetails: any = {};
  // Starred fields left blank on the last Save. Matches the applicant form's
  // required fields; the API also refuses a blank certificate number, and one
  // already used by another endorsement.
  public missingFields: string[] = [];
  private requiredBasicFields = [
    "firstName",
    "cprsCertificateNumber",
    "email",
    "phoneNumber",
    "addressLine1",
  ];

  // Application Edit History. Every inline Save appends one record per changed
  // field to data.History — the same shape the state-to-state preview writes
  // and reads — and the panel groups those records by (editor, save time) so a
  // single Save reads as "You Made N changes".
  public editedUserDetails: any[] = [];
  public collapseIdx = "";

  // Labels shown in the old -> new diff, matching the inline edit form.
  private basicDetailLabels: any = {
    firstName: "First Name",
    lastName: "Last Name",
    preferredName: "Preferred Name",
    cprsCertificateNumber: "CPRS Certificate",
    email: "Email",
    phoneNumber: "Phone",
    addressLine1: "Address",
    city: "City",
    state: "State",
  };

  public statusOptions = [
    { text: "Processing", value: "PROCESSING" },
    { text: "Accepted", value: "ACCEPTED" },
    { text: "Not Accepted", value: "NOT ACCEPTED" },
    { text: "Need More Info", value: "NEED MORE INFO" },
  ];

  public trainingStatusOptions = [
    { text: "Not Started", value: "NOT_STARTED" },
    { text: "In Progress", value: "IN_PROGRESS" },
    { text: "Completed Training", value: "COMPLETED" },
    { text: "Not Completed", value: "NOT_COMPLETED" },
  ];

  public showTrainingDropdown = false;

  get trainingStatusLabel() {
    const opt = this.trainingStatusOptions.find(
      (o) => o.value === this.trainingStatus
    );
    return opt ? opt.text : "-- Select Training Status --";
  }

  public toggleTrainingDropdown() {
    this.showTrainingDropdown = !this.showTrainingDropdown;
  }

  public selectTrainingStatus(option: { text: string; value: string }) {
    this.trainingStatus = option.value;
    this.showTrainingDropdown = false;
  }

  // Colour class per training status: grey / yellow / green / red.
  public trainingStatusClass(value: string) {
    if (value === "IN_PROGRESS") return "ts-progress";
    if (value === "COMPLETED") return "ts-completed";
    if (value === "NOT_STARTED") return "ts-notstarted";
    if (value === "NOT_COMPLETED") return "ts-notcompleted";
    return "";
  }

  // Decision dropdown (mirrors the Training Status dropdown / other forms).
  public showDecisionDropdown = false;

  get decisionLabel() {
    const opt = this.statusOptions.find((o) => o.value === this.approveStatus);
    return opt ? opt.text : "-- Select Decision --";
  }

  public toggleDecisionDropdown() {
    if (this.isDecisionLocked) return;
    this.showDecisionDropdown = !this.showDecisionDropdown;
  }

  public selectDecision(option: { text: string; value: string }) {
    this.approveStatus = option.value;
    this.showDecisionDropdown = false;
  }

  public async created() {
    this.userDetails = utils.getUserDetails();
    if (this.isUserApplication && this.applicationPreviewForm) {
      this.populateFromData(this.applicationPreviewForm);
      this.isLoading = false;
      return;
    }
    this.applicantId = this.$route.path.split("/")[3];
    await this.loadApplication();
  }

  public async loadApplication() {
    this.isLoading = true;
    try {
      const response = await endorsementFormApi.getEndorsementApplication(
        this.applicantId
      );
      this.populateFromData(response.data);
    } catch (error) {
      this.errorMessage = utils.errorMessage(error);
    } finally {
      this.isLoading = false;
    }
  }

  private populateFromData(applicationData: any) {
    this.applicationData = applicationData;
    this.formData = this.applicationData?.data || {};
    this.trainingStatus = this.applicationData?.trainingStatus || "";
    this.approveStatus = this.applicationData?.progressStatus || "";

    // Pre-fill the editable Comment box with the saved review comment; the
    // reviewer status history comes from the ?id= detail response.
    this.approveComment = this.applicationData?.comment || "";
    const tasks = Array.isArray(this.applicationData?.userTask)
      ? this.applicationData.userTask
      : [];
    this.historys = tasks
      .slice()
      .sort(
        (a: any, b: any) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
      .map((t: any) => ({
        ...t,
        createdAt: moment.utc(t.createdAt).local().format("MM-DD-YYYY hh:mm A"),
      }))
      .reverse();
    if (this.applicationData?.rubric) {
      const rubric =
        typeof this.applicationData.rubric === "string"
          ? JSON.parse(this.applicationData.rubric)
          : this.applicationData.rubric;
      this.trainingDates = rubric.trainingDates || "";
      this.trainingLocation = rubric.location || "";
      this.trainingCertificate = rubric.trainingCertificate || "";
      this.trainingStatus = rubric.trainingStatus || this.trainingStatus;
    }
    // The issued certificate comes from the application's own columns, not the
    // rubric — see EndorsementApplicationModel.certificateNumber/certificateFile.
    const issuedFile = this.applicationData?.certificateFile;
    this.issuedCertificateFile = issuedFile || null;
    if (issuedFile && issuedFile.uploadedFileName) {
      this.issuedCertificateAttachment = {
        payloadName: "endorsementCertificate",
        files: [
          {
            fileName: issuedFile.uploadedFileName,
            // The reviewer's own filename, so a download is not a bare UUID.
            originalFileName:
              issuedFile.originalFileName || issuedFile.uploadedFileName,
          },
        ],
      };
    }
    // Restore the calendar selection from a saved "start to end" string.
    if (this.trainingDates && this.trainingDates.includes(" to ")) {
      const [start, end] = this.trainingDates.split(" to ");
      this.trainingDateRange = {
        startDate: new Date(start),
        endDate: new Date(end),
      } as dateRange;
    }
    // Show an already-uploaded certificate in the file uploader.
    if (this.trainingCertificate) {
      this.trainingCertificateAttachment = {
        payloadName: "trainingCertificate",
        files: [
          {
            fileName: this.trainingCertificate,
            originalFileName: this.trainingCertificate,
          },
        ],
      };
    }
    this.buildEditHistory();
  }

  private sectionLabel(parentName: string) {
    switch (parentName) {
      case "endorsementBasicDetails":
        return "Basic Details";
      case "endorsementJusticeInvolvement":
        return "Justice Involvement";
      case "endorsementSupervisor":
        return "Supervisor Details";
      case "endorsementSignature":
        return "Signature";
      default:
        return "Application";
    }
  }

  /**
   * Flattens data.History into one row per (editor, save), newest first, with
   * the changed fields grouped under their section heading.
   */
  private buildEditHistory() {
    this.collapseIdx = "";
    this.editedUserDetails = [];
    const history = Array.isArray(this.formData?.History)
      ? this.formData.History
      : [];
    const changes: any[] = [];
    history.forEach((entry: any) => {
      Object.keys(entry || {}).forEach((fieldName: string) => {
        const fieldChanges = entry[fieldName];
        if (!Array.isArray(fieldChanges)) {
          return;
        }
        fieldChanges.forEach((change: any) => {
          // Records still being edited carry no timestamp; only saved ones
          // belong in the history.
          if (change && change.editDataTime) {
            changes.push({ ...change, fieldName });
          }
        });
      });
    });

    changes.forEach((change: any) => {
      const editorUserId = change.editorDetails?.userId || "";
      const mainLabel = this.sectionLabel(change.parentName);
      let group = this.editedUserDetails.find(
        (edited: any) =>
          edited.editorUserId === editorUserId &&
          edited.dateTime === change.editDataTime
      );
      if (!group) {
        group = {
          editorUserId,
          editorName: change.editorDetails?.name || "",
          dateTime: change.editDataTime,
          changesCount: 0,
          data: [],
        };
        this.editedUserDetails.push(group);
      }
      group.changesCount = group.changesCount + 1;
      const section = group.data.find(
        (data: any) => data.mainLabel === mainLabel
      );
      if (section) {
        section.changesData.push(change);
      } else {
        group.data.push({ mainLabel, changesData: [change] });
      }
    });

    this.editedUserDetails.sort(
      (a: any, b: any) =>
        new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()
    );
    this.editedUserDetails.forEach((edited: any) => {
      edited.dateTime = moment(edited.dateTime).format(
        "MM-DD-YYYY [|] hh:mm A"
      );
    });
  }

  /**
   * Returns data.History with this save's changes appended. Values are compared
   * against the saved application, so an untouched field adds nothing.
   */
  private appendEditHistory() {
    const history = Array.isArray(this.formData?.History)
      ? JSON.parse(JSON.stringify(this.formData.History))
      : [];
    // One timestamp for the whole save — that is what groups the fields into a
    // single "Made N changes" row.
    const editDataTime = moment().format();
    const editorDetails = {
      name: this.userDetails?.displayName || "",
      email: this.userDetails?.email || "",
      userId: this.userDetails?.userId || "",
    };
    Object.keys(this.editBasicDetails).forEach((fieldName: string) => {
      const oldValue = this.basicDetails[fieldName] ?? "";
      const newValue = this.editBasicDetails[fieldName] ?? "";
      if (String(oldValue) === String(newValue)) {
        return;
      }
      const change = {
        oldValue,
        newValue,
        label: this.basicDetailLabels[fieldName] || fieldName,
        fieldName,
        parentName: "endorsementBasicDetails",
        editorDetails,
        editDataTime,
      };
      const existing = history.find((entry: any) => entry[fieldName]);
      if (existing) {
        existing[fieldName].push(change);
      } else {
        history.push({ [fieldName]: [change] });
      }
    });
    return history;
  }

  // FileUploadComponent emits the chosen file(s). With fileNoNeedUpload they are
  // raw File objects (uploaded later on Submit); an already-uploaded file keeps
  // its S3 fileName.
  public onTrainingDateUpdate() {
    if (this.trainingDateRange?.startDate && this.trainingDateRange?.endDate) {
      const start = moment(new Date(this.trainingDateRange.startDate)).format(
        "MM-DD-YYYY"
      );
      const end = moment(new Date(this.trainingDateRange.endDate)).format(
        "MM-DD-YYYY"
      );
      this.trainingDates = `${start} to ${end}`;
    }
  }

  get basicDetails() {
    return this.formData?.endorsementBasicDetails || {};
  }

  get justiceInvolvement() {
    return this.formData?.endorsementJusticeInvolvement || {};
  }

  get supervisorDetails() {
    return this.formData?.endorsementSupervisor || {};
  }

  get signatureDetails() {
    return this.formData?.endorsementSignature || {};
  }

  get hasJustice() {
    return Object.keys(this.justiceInvolvement).length > 0;
  }

  // Once a final decision is made (Accepted / Not Accepted) the reviewer can no
  // longer change it — the Admin Review controls become read-only.
  get isDecisionLocked(): boolean {
    const status = (this.applicationData?.progressStatus || "").toUpperCase();
    return status === STATUS.accepted || status === STATUS.notAccepted;
  }

  // Edit availability: reviewers can always inline-edit; applicants (My
  // Applications) can edit their own application until a final decision is made
  // — mirroring Part Two.
  get showEditAction(): boolean {
    // No edits once a final decision (Accepted / Not Accepted) is made —
    // for reviewers and the applicant alike. Otherwise both can edit.
    return !this.isDecisionLocked;
  }

  // True only when an edited field differs from the original — drives the
  // Save button's enabled state.
  get isEditDirty() {
    return Object.keys(this.editBasicDetails).some(
      (key) =>
        (this.editBasicDetails[key] ?? "") !== (this.basicDetails[key] ?? "")
    );
  }

  get applicantFullName() {
    return (
      `${this.basicDetails.firstName || ""} ${
        this.basicDetails.lastName || ""
      }`.trim() ||
      `${this.applicationData?.firstName || ""} ${
        this.applicationData?.lastName || ""
      }`.trim()
    );
  }

  get formattedSubmittedAt() {
    return this.applicationData?.submittedAt
      ? moment(this.applicationData.submittedAt).format("MM-DD-YYYY")
      : "—";
  }

  get statusClass() {
    const status = (this.applicationData?.progressStatus || "").toLowerCase();
    if (status === "accepted") return "accepted";
    if (status === "not accepted") return "rejected";
    if (status === "processing") return "processing";
    return "received";
  }

  get isImageSignature() {
    const sig = this.signatureDetails?.isSignature || "";
    return typeof sig === "string" && sig.startsWith("data:image");
  }

  public async submitReview() {
    if (this.isDecisionLocked) {
      return;
    }
    // Decision is optional: a reviewer can submit after only updating the
    // training status. approveStatus is pre-seeded with the existing status
    // (created()), so leaving the decision untouched re-sends the current
    // status rather than clearing it.
    this.submitError = "";
    this.isSubmitting = true;
    // The certificate is part of this panel, so it saves with the panel - the
    // reviewer presses Submit Review once, not once per section. Sent first so a
    // failure (a number another applicant already holds) stops the review rather
    // than leaving the decision saved and the certificate not.
    if (!(await this.persistCertificate())) {
      this.isSubmitting = false;
      return;
    }
    const command = {
      id: this.applicantId,
      status: this.approveStatus,
      comment: this.approveComment,
      rubric: {
        trainingStatus: this.trainingStatus,
        trainingDates: this.trainingDates,
        location: this.trainingLocation,
        trainingCertificate: this.trainingCertificate,
      },
    };
    endorsementFormApi
      .approveEndorsementApplication(command)
      .then(() => {
        this.isSubmitting = false;
        // Return to the endorsement list, which refetches the table on load.
        this.$router.push(this.returnRoute);
      })
      .catch((error) => {
        this.submitError = utils.errorMessage(error);
        this.isSubmitting = false;
      });
  }

  public startEdit() {
    // Work on a copy so Cancel can discard changes.
    this.editBasicDetails = { ...this.basicDetails };
    this.missingFields = [];
    this.isEditing = true;
  }

  public clearMissing(key: string) {
    this.missingFields = this.missingFields.filter((k) => k !== key);
  }

  public cancelEdit() {
    this.isEditing = false;
    this.editBasicDetails = {};
  }

  public saveEdit() {
    this.missingFields = this.requiredBasicFields.filter(
      (key) => !String(this.editBasicDetails[key] ?? "").trim()
    );
    if (this.missingFields.length) {
      return;
    }
    this.savingEdit = true;
    // Diff before the local copy is overwritten below, so the history records
    // the values as they were saved.
    const history = this.appendEditHistory();
    const payLoadData = {
      endorsementBasicDetails: this.editBasicDetails,
      endorsementJusticeInvolvement: this.justiceInvolvement,
      endorsementSupervisor: this.supervisorDetails,
      endorsementSignature: this.signatureDetails,
      History: history,
      editedComments: this.formData?.editedComments || [],
    };
    const id = this.applicationData?.id || this.applicantId;
    // Applicants edit their own application via the applicant endpoint;
    // reviewers use the admin endpoint.
    const request = this.isUserApplication
      ? endorsementFormApi.updateEndorsementApplicationByApplicant(
          payLoadData,
          id
        )
      : endorsementFormApi.updateEndorsementApplicationByAdmin(payLoadData, id);
    request
      .then(() => {
        this.isEditing = false;
        this.savingEdit = false;
        // Reflect the saved values immediately; the applicant view has no route
        // id to refetch from, so update the local copy instead.
        if (this.formData) {
          this.formData.endorsementBasicDetails = { ...this.editBasicDetails };
          this.formData.History = history;
          this.buildEditHistory();
        }
        if (this.applicantId) {
          this.loadApplication();
        }
      })
      .catch((error) => {
        this.errorMessage = utils.errorMessage(error);
        this.savingEdit = false;
      });
  }

  public goBack() {
    this.$router.push(this.returnRoute);
  }

  public closdedErrorPopup(event: any) {
    if (event) {
      this.errorMessage = "";
    }
  }

  /*
   * Attaches the endorsement certificate as part of the review submit. Returns
   * false when the save failed, so the caller can stop before recording the
   * decision.
   *
   * Only a newly picked file triggers a save; an ordinary review submit sends
   * nothing. No number goes with it: the certificate is issued against the CPRS
   * number the applicant gave on the application, which the API reads from the
   * application itself.
   */
  private async persistCertificate(): Promise<boolean> {
    const files = this.issuedCertificateFiles.filter(
      (file: any) => file instanceof File
    );
    if (!files.length) {
      return true;
    }
    try {
      await endorsementFormApi.endorsementCertificateUpdate(
        this.applicationData.id,
        files
      );
      this.issuedCertificateFiles = [];
      return true;
    } catch (error) {
      this.submitError = utils.errorMessage(error);
      return false;
    }
  }
}
